import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** Win rate (0-100) over completed matches only: (wins + 0.5 * draws) / (wins + losses + draws). Null when none completed. */
export function winRate({ wins, losses, draws }: { wins: number; losses: number; draws?: number }): number | null {
  const d = draws ?? 0;
  const completed = wins + losses + d;
  return completed > 0 ? ((wins + 0.5 * d) / completed) * 100 : null;
}

export type MatchResult = "W" | "L" | "D";

/** Three-way result of a match. Null while pending (not a result yet). */
export function matchResult(m: { isWin: boolean; isDraw?: boolean; isPending?: boolean }): MatchResult | null {
  if (m.isPending) return null;
  if (m.isDraw) return "D";
  return m.isWin ? "W" : "L";
}

/** Current win/loss streak from results ordered most recent first. Draws and pending neither extend nor break it. */
export function currentStreakOf(results: (MatchResult | null)[]): { count: number; type: "win" | "loss" } | null {
  let type: "win" | "loss" | null = null;
  let count = 0;
  for (const r of results) {
    if (r === "D" || r === null) continue;
    const t = r === "W" ? "win" : "loss";
    if (type === null) type = t;
    if (t !== type) break;
    count++;
  }
  return type ? { count, type } : null;
}

/** Longest win streak from results ordered oldest first. Draws and pending neither extend nor break it. */
export function longestWinStreak(results: (MatchResult | null)[]): number {
  let longest = 0;
  let current = 0;
  for (const r of results) {
    if (r === "D" || r === null) continue;
    if (r === "W") { current++; longest = Math.max(longest, current); } else current = 0;
  }
  return longest;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
